import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {deleteRecord, fetchRecords, send} from '../services/InvoicesService';
import {useState, useEffect} from 'react';
import {Invoice} from '../types';
import {usePagination} from '../../../hooks/usePagination';
import {notifyError, notifySuccess} from '../../../components/ServiceNotifications';

export const useInvoices = (customerId?: string) => {
    const queryClient = useQueryClient();
    const [keyword, setKeyword] = useState('');
    const pagination = usePagination();

    const filter = customerId ? {customerId: customerId} : {};
    const queryKey = customerId ? ['invoices', {customerId: customerId}, pagination.currentToken] : ['invoices', pagination.currentToken];

    const invoicesQuery = useQuery({
        queryKey,
        queryFn: () => fetchRecords(filter, pagination.pageSize, pagination.currentToken),
    });

    useEffect(() => {
        if (invoicesQuery.data) pagination.setNextToken(invoicesQuery.data.nextToken);
    }, [invoicesQuery.data]);

    useEffect(() => {
        pagination.resetPagination();
    }, [customerId]);

    const deleteMutation = useMutation({
        mutationFn: async (ids: string[]) => {
            const promises = ids.map(id => deleteRecord(id));
            return Promise.all(promises);
        },
        onSuccess: () => {
            notifySuccess('Invoice deleted');
            queryClient.invalidateQueries({queryKey: ['invoices']});
        },
        onError: notifyError,
    });

    const sendMutation = useMutation({
        mutationFn: async (ids: string[]) => {
            const promises = ids.map(id => send(id));
            return Promise.all(promises);
        },
        onSuccess: () => {
            notifySuccess('Invoice sent');
            queryClient.invalidateQueries({queryKey: ['invoices']});
        },
        onError: notifyError,
    });

    const fetchRecordsAction = () => {
        queryClient.invalidateQueries({queryKey: ['invoices']});
    };

    const deleteRecords = (ids?: string[]) => {
        if (ids && ids.length > 0) {
            deleteMutation.mutate(ids);
        }
    };

    const sendInvoices = (ids?: string[]) => {
        if (ids && ids.length > 0) {
            sendMutation.mutate(ids);
        }
    };

    const handleFilterByKeyword = (newKeyword: string) => {
        setKeyword(newKeyword);
    };

    const allItems: Invoice[] = invoicesQuery.data?.items || [];

    const visibleItems = keyword
        ? allItems.filter(item => {
            const search = keyword.toLowerCase();
            return (
                item.id?.toLowerCase().includes(search) ||
                item.customerId?.toLowerCase().includes(search) ||
                item.customerName?.toLowerCase().includes(search) ||
                item.description?.toLowerCase().includes(search) ||
                item.status?.toLowerCase().includes(search) ||
                item.number?.toLowerCase().includes(search)
            );
        })
        : allItems;

    return {
        items: allItems,
        visibleItems,
        loading: invoicesQuery.isLoading,
        keyword,
        visibleCount: visibleItems.length,
        totalCount: allItems.length,
        fetchRecords: fetchRecordsAction,
        deleteRecords,
        sendInvoices,
        handleFilterByKeyword,
        deleteMutation,
        sendMutation,
        currentPage: pagination.currentPage,
        hasNext: pagination.hasNext,
        hasPrevious: pagination.hasPrevious,
        goToNextPage: pagination.goToNextPage,
        goToPreviousPage: pagination.goToPreviousPage,
    };
};
